import type { TolgeeInstance } from '@tolgee/react';

// The Tolgee Tools browser extension stores credentials in sessionStorage and
// reloads the page to start an in-context editing session (same keys as
// BrowserExtensionPlugin in @tolgee/web).
const API_KEY_STORAGE_KEY = '__tolgee_apiKey';
const API_URL_STORAGE_KEY = '__tolgee_apiUrl';

interface IInContextCredentials {
  apiKey: string;
  apiUrl: string;
}

/**
 * Read and REMOVE the extension-provided credentials before the Tolgee instance
 * is constructed (this module is imported by tolgee.ts, so it evaluates first).
 *
 * If the credentials stayed in sessionStorage, the SDK's built-in
 * BrowserExtensionPlugin would see them at construction time and inject a
 * SECOND copy of the in-context tools from cdn.jsdelivr.net pinned to the
 * unstable "prerelease" version. That copy races the pinned copy we ship, and
 * when it wins (reliably in dev builds) its UI is incompatible with the core in
 * the bundle — ALT+click then silently does nothing. Stashing the credentials
 * keeps that code path dormant so our pinned tools are the only ones running.
 */
const stashedCredentials: IInContextCredentials | undefined = (() => {
  try {
    const apiKey = window.sessionStorage.getItem(API_KEY_STORAGE_KEY);
    const apiUrl = window.sessionStorage.getItem(API_URL_STORAGE_KEY);
    if (apiKey && apiUrl) {
      window.sessionStorage.removeItem(API_KEY_STORAGE_KEY);
      window.sessionStorage.removeItem(API_URL_STORAGE_KEY);
      return { apiKey, apiUrl };
    }
  } catch {
    // sessionStorage unavailable (e.g. some webviews) — no session possible
  }
  return undefined;
})();

/**
 * In-context editing (ALT/Option+click on labels) for translators using the
 * Tolgee Tools browser extension.
 *
 * Ships in every build as a lazy chunk that is imported ONLY when the extension
 * has provided credentials — regular users and the Capacitor webviews never load
 * it and never talk to the Tolgee server.
 */
export const addInContextToolsIfRequested = (tolgee: TolgeeInstance): void => {
  if (!stashedCredentials) {
    return;
  }

  // eslint-disable-next-line no-console
  console.info('[tolgee-in-context] session credentials detected, loading tools');

  // Put the keys back for the extension's own lifecycle: session persistence
  // across reloads and the "turn off" flow (which clears them). The SDK only
  // reads them at instance construction, which has already happened.
  try {
    window.sessionStorage.setItem(API_KEY_STORAGE_KEY, stashedCredentials.apiKey);
    window.sessionStorage.setItem(API_URL_STORAGE_KEY, stashedCredentials.apiUrl);
  } catch {
    // Session simply won't survive a reload — editing still works on this page.
  }

  import('@tolgee/web/tools')
    .then((tools) => {
      // addPlugin internally restarts the running instance, which reloads the
      // records through the dev backend and re-renders every label wrapped.
      // Never restart again after this: a second restart creates a fresh
      // invisible-encoding memory, the already-wrapped DOM decodes to null
      // keys, and ALT+click silently opens nothing.
      tolgee.addPlugin(tools.InContextTools({ credentials: stashedCredentials }));
      // eslint-disable-next-line no-console
      console.info('[tolgee-in-context] tools attached');
      installClickOpener(tolgee, stashedCredentials, tools.ContextUi);
    })
    .catch((error) => {
      // Translator tooling only — regular rendering is unaffected. Loudly
      // logged: a silent failure here looks like "ALT+click does nothing".
      // eslint-disable-next-line no-console
      console.error('[tolgee-in-context] failed to activate in-context tools', error);
    });
};

interface IUiKeyOption {
  key: string;
  namespace: string;
  fallbackNamespaces: string[];
  translation?: string;
  defaultValue?: string;
}

interface IUiInstance {
  handleElementClick(keys: IUiKeyOption[], target: Element): unknown;
}

type tUiMiddleware = (props: Record<string, unknown>) => IUiInstance;

interface IUiPluginTools {
  setUi(middleware: unknown): void;
  hasUi(): boolean;
  hasObserver(): boolean;
  setObserver(observer: unknown): void;
  setDevBackend(backend: unknown): void;
  addBackend(backend: unknown): void;
  setFinalFormatter(formatter: unknown): void;
  addFormatter(formatter: unknown): void;
  setLanguageDetector(detector: unknown): void;
  setLanguageStorage(storage: unknown): void;
}

/**
 * Opens the translation dialog on ALT/Option+click ourselves instead of relying
 * on the SDK's internal click→ui hand-off (`instances.ui?.handleElementClick`),
 * which can silently lose its ui instance across plugin restarts. We hit-test
 * the click against the observer's registered label positions and call the same
 * dialog entry point directly with a UI instance we hold on to.
 */
function installClickOpener(
  tolgee: TolgeeInstance,
  credentials: IInContextCredentials,
  ContextUi: () => unknown
): void {
  let uiInstance: IUiInstance | undefined;

  const getUi = (): IUiInstance => {
    if (!uiInstance) {
      let middleware: tUiMiddleware | undefined;
      const uiTools: IUiPluginTools = {
        setUi: (m) => {
          middleware = m as tUiMiddleware;
        },
        hasUi: () => false,
        hasObserver: () => true,
        setObserver: () => undefined,
        setDevBackend: () => undefined,
        addBackend: () => undefined,
        setFinalFormatter: () => undefined,
        addFormatter: () => undefined,
        setLanguageDetector: () => undefined,
        setLanguageStorage: () => undefined
      };
      const applyPlugin = ContextUi() as (
        instance: unknown,
        tools: IUiPluginTools
      ) => unknown;
      applyPlugin(
        { addPlugin: () => undefined, on: () => ({ unsubscribe: () => undefined }) },
        uiTools
      );
      if (!middleware) {
        throw new Error('ContextUi did not provide a ui middleware');
      }
      uiInstance = middleware({
        apiUrl: credentials.apiUrl,
        apiKey: credentials.apiKey,
        highlight: (key?: string, ns?: string) => tolgee.highlight(key, ns),
        changeTranslation: (
          descriptor: Parameters<TolgeeInstance['changeTranslation']>[0],
          key: string,
          value: string
        ) => tolgee.changeTranslation(descriptor, key, value),
        findPositions: (key?: string, ns?: string) => tolgee.findPositions(key, ns),
        onPermanentChange: () => undefined
      });
    }
    return uiInstance;
  };

  document.addEventListener(
    'click',
    (event) => {
      if (!event.altKey) return;
      const hit = tolgee.findPositions().find(({ position }) => {
        return (
          event.clientX >= position.x &&
          event.clientX <= position.x + position.width &&
          event.clientY >= position.y &&
          event.clientY <= position.y + position.height
        );
      });
      if (!hit) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      try {
        getUi().handleElementClick(
          [
            {
              key: hit.keyName,
              namespace: hit.keyNamespace ?? '',
              fallbackNamespaces: [],
              translation: undefined,
              defaultValue: undefined
            }
          ],
          (event.target as Element | null) ?? document.body
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[tolgee-in-context] failed to open the dialog', error);
      }
    },
    true
  );
}
